// =============================================================================
// OperacaoComentarios - Central de Comunicação (Fase A+B): thread por operação
// =============================================================================
// Comentários internos (não vão ao cliente): memória compartilhada da operação,
// visível a quem a assumir. Lista + composer com menção @ (autocomplete sobre os
// usuários ativos). O mencionado recebe uma notificação (sino). Editar/excluir na
// Fase C.
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquare, Send, Pencil, Trash2 } from 'lucide-react';
import { comentariosApi, usuariosApi } from '../services/api';
import { useCurrentUser } from '../hooks/useCurrentUser';
import ConfirmModal from './ConfirmModal';
import type { ComentarioOperacao, UsuarioMencionavel } from '../types';

interface Props {
  operacaoId: string;
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  return ((partes[0]?.[0] ?? '') + (partes.length > 1 ? partes[partes.length - 1][0] : '')).toUpperCase() || '?';
}

function quando(iso: string): string {
  const d = new Date(iso);
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `há ${diffMin} min`;
  if (diffMin < 1440) return `há ${Math.floor(diffMin / 60)} h`;
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// Realça "@Nome" para cada usuário mencionado no comentário.
function renderCorpo(corpo: string, nomes: string[]): React.ReactNode {
  if (nomes.length === 0) return corpo;
  const escaped = nomes.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`@(${escaped.join('|')})`, 'g');
  const out: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(corpo)) !== null) {
    if (m.index > last) out.push(corpo.slice(last, m.index));
    out.push(<span key={`m${i++}`} className="text-otm-navy font-medium">@{m[1]}</span>);
    last = m.index + m[0].length;
  }
  if (last < corpo.length) out.push(corpo.slice(last));
  return out;
}

// Detecta o token de menção em digitação: um '@' no início ou após espaço,
// seguido só de caracteres de nome (sem espaço) até o cursor.
function detectarMencao(texto: string, caret: number): { query: string; inicio: number } | null {
  const ate = texto.slice(0, caret);
  const m = ate.match(/(?:^|\s)@([\p{L}0-9._-]*)$/u);
  if (!m) return null;
  const query = m[1];
  return { query, inicio: caret - query.length - 1 };
}

export default function OperacaoComentarios({ operacaoId }: Props) {
  const [comentarios, setComentarios] = useState<ComentarioOperacao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);

  const [usuarios, setUsuarios] = useState<UsuarioMencionavel[]>([]);
  const [mencionados, setMencionados] = useState<{ id: string; nome: string }[]>([]);
  const [menu, setMenu] = useState<{ query: string; inicio: number } | null>(null);

  // Fase C - editar/excluir (autor ou ADMIN)
  const { currentUser } = useCurrentUser();
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [textoEdit, setTextoEdit] = useState('');
  const [salvandoEdit, setSalvandoEdit] = useState(false);
  const [aExcluir, setAExcluir] = useState<ComentarioOperacao | null>(null);
  const [excluindo, setExcluindo] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fimRef = useRef<HTMLDivElement>(null);

  const podeGerir = (c: ComentarioOperacao) =>
    !!currentUser && (c.autor.id === currentUser.id || currentUser.papel === 'ADMIN');

  const salvarEdicao = async (c: ComentarioOperacao) => {
    const corpo = textoEdit.trim();
    if (!corpo || salvandoEdit) return;
    setSalvandoEdit(true);
    try {
      const atualizado = await comentariosApi.editar(operacaoId, c.id, corpo);
      setComentarios((prev) => prev.map((x) => (x.id === c.id ? atualizado : x)));
      setEditandoId(null);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { erro?: string } } })?.response?.data?.erro;
      setErro(msg ?? 'Não foi possível salvar a edição.');
    } finally {
      setSalvandoEdit(false);
    }
  };

  const confirmarExclusao = async () => {
    if (!aExcluir) return;
    setExcluindo(true);
    try {
      await comentariosApi.excluir(operacaoId, aExcluir.id);
      setComentarios((prev) => prev.filter((x) => x.id !== aExcluir.id));
      setAExcluir(null);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { erro?: string } } })?.response?.data?.erro;
      setErro(msg ?? 'Não foi possível excluir o comentário.');
      setAExcluir(null);
    } finally {
      setExcluindo(false);
    }
  };

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    setErro(null);
    comentariosApi
      .listar(operacaoId)
      .then((c) => { if (ativo) setComentarios(c); })
      .catch(() => { if (ativo) setErro('Não foi possível carregar os comentários.'); })
      .finally(() => { if (ativo) setCarregando(false); });
    return () => { ativo = false; };
  }, [operacaoId]);

  // Usuários mencionáveis (uma vez; falha silenciosa só desabilita o @)
  useEffect(() => {
    usuariosApi.mencionaveis().then(setUsuarios).catch(() => setUsuarios([]));
  }, []);

  const opcoes = useMemo(() => {
    if (!menu) return [];
    const q = menu.query.toLowerCase();
    return usuarios.filter((u) => u.nome.toLowerCase().includes(q)).slice(0, 6);
  }, [menu, usuarios]);

  const aoDigitar = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    setTexto(v);
    const caret = e.target.selectionStart ?? v.length;
    setMenu(detectarMencao(v, caret));
  };

  const escolherMencao = (u: UsuarioMencionavel) => {
    if (!menu) return;
    const antes = texto.slice(0, menu.inicio);
    const depois = texto.slice(menu.inicio + 1 + menu.query.length);
    const novo = `${antes}@${u.nome} ${depois}`;
    setTexto(novo);
    setMencionados((prev) => (prev.some((m) => m.id === u.id) ? prev : [...prev, { id: u.id, nome: u.nome }]));
    setMenu(null);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const enviar = async () => {
    const corpo = texto.trim();
    if (!corpo || enviando) return;
    // só menções cujo "@Nome" ainda está no texto
    const ids = [...new Set(mencionados.filter((m) => corpo.includes(`@${m.nome}`)).map((m) => m.id))];
    setEnviando(true);
    setErro(null);
    try {
      const novo = await comentariosApi.criar(operacaoId, corpo, ids);
      setComentarios((prev) => [...prev, novo]);
      // Houve menção → o sino do topbar pode ter ganhado aviso (inclusive p/ quem
      // se menciona): pede refetch imediato da contagem, sem esperar o poll.
      if (ids.length) window.dispatchEvent(new Event('notificacoes:atualizar'));
      setTexto('');
      setMencionados([]);
      setMenu(null);
      setTimeout(() => fimRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { erro?: string } } })?.response?.data?.erro;
      setErro(msg ?? 'Não foi possível enviar o comentário.');
    } finally {
      setEnviando(false);
    }
  };

  const aoTeclar = (e: React.KeyboardEvent) => {
    if (menu && opcoes.length > 0 && e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      escolherMencao(opcoes[0]);
      return;
    }
    if (e.key === 'Escape' && menu) { setMenu(null); return; }
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); enviar(); }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Thread (rolável) */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {carregando ? (
          <p className="text-sm text-slate-400 text-center py-8">Carregando comentários…</p>
        ) : comentarios.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center text-slate-400">
            <MessageSquare size={28} className="mb-2" />
            <p className="text-sm">Nenhum comentário ainda.</p>
            <p className="text-xs">Registre aqui o que outro operador precisa saber sobre esta operação.</p>
          </div>
        ) : (
          comentarios.map((c) => (
            <div key={c.id} className="group flex gap-2.5">
              <div className="shrink-0 w-8 h-8 rounded-full bg-otm-navy/10 text-otm-navy flex items-center justify-center text-xs font-semibold">
                {iniciais(c.autor.nome)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-semibold text-slate-800 truncate">{c.autor.nome}</span>
                  <span className="text-[11px] text-slate-400 shrink-0" title={new Date(c.createdAt).toLocaleString('pt-BR')}>
                    {quando(c.createdAt)}{c.editadoEm ? ' · editado' : ''}
                  </span>
                  {podeGerir(c) && editandoId !== c.id && (
                    <span className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button type="button" title="Editar" onClick={() => { setEditandoId(c.id); setTextoEdit(c.corpo); }}
                        className="p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100">
                        <Pencil size={13} />
                      </button>
                      <button type="button" title="Excluir" onClick={() => setAExcluir(c)}
                        className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50">
                        <Trash2 size={13} />
                      </button>
                    </span>
                  )}
                </div>

                {editandoId === c.id ? (
                  <div className="mt-1">
                    <textarea
                      value={textoEdit}
                      onChange={(e) => setTextoEdit(e.target.value)}
                      rows={2}
                      className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-otm-navy/30 focus:border-otm-navy"
                    />
                    <div className="flex items-center gap-2 mt-1">
                      <button type="button" onClick={() => salvarEdicao(c)} disabled={salvandoEdit || !textoEdit.trim()}
                        className="text-xs font-semibold text-white bg-otm-orange hover:bg-otm-orange-dark px-3 py-1.5 rounded-lg disabled:opacity-50">
                        {salvandoEdit ? 'Salvando…' : 'Salvar'}
                      </button>
                      <button type="button" onClick={() => setEditandoId(null)} disabled={salvandoEdit}
                        className="text-xs font-medium text-slate-500 hover:text-slate-700 px-2 py-1.5">
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-700 whitespace-pre-wrap break-words mt-0.5">
                    {renderCorpo(c.corpo, c.mencoes.map((m) => m.usuario.nome))}
                  </p>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={fimRef} />
      </div>

      {/* Composer (fixo no rodapé da aba) */}
      <div className="relative shrink-0 border-t border-slate-100 p-3 bg-white">
        {/* Dropdown de menção (acima do textarea) */}
        {menu && opcoes.length > 0 && (
          <div className="absolute bottom-full left-3 mb-1 w-64 max-h-52 overflow-y-auto bg-white border border-otm-border rounded-lg shadow-lg py-1 z-10">
            {opcoes.map((u) => (
              <button
                key={u.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); escolherMencao(u); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-otm-subtle transition-colors"
              >
                <span className="shrink-0 w-6 h-6 rounded-full bg-otm-navy/10 text-otm-navy flex items-center justify-center text-[10px] font-semibold">
                  {iniciais(u.nome)}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm text-slate-800 truncate">{u.nome}</span>
                  <span className="block text-[11px] text-slate-400 truncate">{u.email}</span>
                </span>
              </button>
            ))}
          </div>
        )}

        {erro && <p className="text-xs text-red-600 mb-2">{erro}</p>}
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={texto}
            onChange={aoDigitar}
            onKeyDown={aoTeclar}
            rows={2}
            placeholder="Escreva um comentário interno… ( @ menciona · Ctrl+Enter envia )"
            className="flex-1 resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-otm-navy/30 focus:border-otm-navy transition-colors"
          />
          <button
            type="button"
            onClick={enviar}
            disabled={enviando || !texto.trim()}
            className="inline-flex items-center gap-1.5 shrink-0 text-sm font-semibold text-white bg-otm-orange hover:bg-otm-orange-dark px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            <Send size={15} />{enviando ? 'Enviando…' : 'Comentar'}
          </button>
        </div>
      </div>

      <ConfirmModal
        aberto={!!aExcluir}
        titulo="Excluir comentário?"
        mensagem="O comentário deixará de aparecer na operação. Esta ação não pode ser desfeita."
        textoConfirmar="Excluir"
        tom="perigo"
        processando={excluindo}
        onConfirmar={confirmarExclusao}
        onCancelar={() => setAExcluir(null)}
      />
    </div>
  );
}
